/**
 * Field-placement diagram geometry (issue #105).
 *
 * Pure maths on a plain object. It mounts nothing, imports nothing from
 * React, and is unit-tested without a DOM — deliberately, because #615 and
 * #617 were both bitten by a rule that lived inside a component and so could
 * not be asserted by anything this repo runs.
 *
 * ## READ THIS BEFORE YOU ADD PER-GRADE GROUND RADII
 *
 * #105 argues at length that a position's depth should be stored as a
 * fraction of the boundary radius. It then records the hazard that falls out
 * of doing that: `Short leg` stands 4 m from the bat because that is where a
 * bat-pad catch happens, not because the ground is 65 m across. Store 4/65
 * and re-multiply it by a junior ground's radius and a child is standing
 * ~2.5 m from another child's bat.
 *
 * Martin's decision (2026-09-02) was: fraction-scale `boundary` and
 * `inner_ring`, keep `close` in ABSOLUTE METRES.
 *
 * **This reader never scales to a grade** — Martin's other decision, 20 Aug:
 * the diagrams are generic and carry a disclaimer saying so. So the hazard is
 * dormant here, not solved. It comes back with #587 (the player-built editor)
 * or any per-grade radius work.
 *
 * What this module does in the meantime is refuse to create the hazard:
 * depths arrive in METRES from the guide JSON and a fraction exists only
 * inside `project()` below, recomputed per render against the drawing
 * radius. Shrink `boundaryM` and every fielder moves in proportionally —
 * INCLUDING the close catchers, which is exactly what must not happen on a
 * junior ground. So if you are here to make `boundaryM` a per-grade value:
 * that is the change that needs the branch on `category === "close"`, and it
 * needs it here, in `project()`. Nothing upstream will save you, because
 * upstream stores the honest metres.
 *
 * ## The angle convention
 *
 * Degrees clockwise from 0 = straight down the pitch toward the bowler.
 * 90 = square on the off side, 180 = the keeper, 270 = square leg. The
 * striker is the ORIGIN — a field placement is measured from the bat, not
 * from the middle of the ground.
 *
 * On screen the bowler's end is UP and the off side is on the RIGHT, which is
 * the view from directly above with the striker at the centre: a
 * right-handed batter faces the off side, so with the bowler at the top of
 * the picture the off side is the right half.
 */

/** Drawn width/height of the square viewBox. Unitless — the SVG scales. */
export const DIAGRAM_SIZE = 320;
/** Padding inside the viewBox so a boundary rider's dot is not clipped. */
export const DIAGRAM_PADDING = 14;
/** The 30-yard circle, in metres. Fixed by the Laws, not by a grade. */
export const INNER_CIRCLE_M = 27.43;
/** Pitch length and width, in metres (22 yards by 10 feet). */
export const PITCH_LENGTH_M = 20.12;
export const PITCH_WIDTH_M = 3.05;

/**
 * Angle -> screen direction. Exported for the tests, which pin the four
 * cardinal anchors rather than restating the trigonometry.
 */
export function unitVector(angleDeg) {
  const radians = (angleDeg * Math.PI) / 180;
  // 0 deg is UP (-y) and angle runs clockwise, so sin drives x and -cos y.
  return { dx: Math.sin(radians), dy: -Math.cos(radians) };
}

/** Left-handed batter: the field mirrors about the pitch. `360 - angle`. */
export function mirrorAngle(angleDeg) {
  const a = ((angleDeg % 360) + 360) % 360;
  return a === 0 ? 0 : 360 - a;
}

/**
 * Metres from the striker -> a point in the viewBox.
 *
 * A fielder deeper than `boundaryM` is drawn ON the rope, not outside it:
 * the guides quote real depths up to 72 m against a 65 m dataset boundary,
 * and a dot in the car park reads as a bug. The exact metres survive in the
 * legend, so nothing is lost — the picture is schematic, the number is not.
 */
export function project(angleDeg, depthM, { boundaryM, radius, cx, cy }) {
  const { dx, dy } = unitVector(angleDeg);
  const drawn = Math.min(depthM, boundaryM) / boundaryM;
  return { x: cx + dx * drawn * radius, y: cy + dy * drawn * radius };
}

/**
 * Build the whole drawable model from a guide's `field_diagram`.
 *
 * Returns `null` for anything it cannot draw HONESTLY — including a diagram
 * with no disclaimer. That is the second half of the guarantee whose first
 * half is the API schema's `z.literal(FIELD_DISCLAIMER)`: the data cannot
 * validate without the sentence, and this cannot draw without it. There is
 * no code path that renders positions and omits the disclaimer.
 */
export function buildFieldDiagramModel(fieldDiagram, options = {}) {
  if (!fieldDiagram || typeof fieldDiagram !== "object") return null;
  const { disclaimer, caption, boundary_m: boundaryM, positions } = fieldDiagram;
  if (!disclaimer || typeof disclaimer !== "string") return null;
  if (!Array.isArray(positions) || positions.length === 0) return null;
  if (!(boundaryM > 0)) return null;

  const size = options.size ?? DIAGRAM_SIZE;
  const handedness = options.handedness === "left" ? "left" : "right";
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - DIAGRAM_PADDING;
  const geometry = { boundaryM, radius, cx, cy };
  const perMetre = radius / boundaryM;

  const fielders = positions.map((position, index) => {
    const angle = handedness === "left" ? mirrorAngle(position.angle) : position.angle;
    return {
      index,
      number: index + 1,
      id: position.id,
      name: position.name,
      angle,
      sourceAngle: position.angle,
      depthM: position.depth_m,
      category: position.distance_category,
      source: position.source,
      // True when the guide's own depth is deeper than the drawn rope, so a
      // renderer can say "on the rope" honestly rather than silently.
      clamped: position.depth_m > boundaryM,
      ...project(angle, position.depth_m, geometry),
    };
  });

  return {
    disclaimer,
    caption: caption ?? "",
    handedness,
    boundaryM,
    size,
    cx,
    cy,
    radius,
    innerCircleR: INNER_CIRCLE_M * perMetre,
    pitch: {
      x: cx - (PITCH_WIDTH_M / 2) * perMetre,
      y: cy - PITCH_LENGTH_M * perMetre,
      width: PITCH_WIDTH_M * perMetre,
      height: PITCH_LENGTH_M * perMetre,
    },
    fielders,
  };
}

/** Legend grouping. Close catchers first — they are what the disclaimer is
 * about, so they are what a reader should meet first. */
export const CATEGORY_ORDER = ["close", "inner_ring", "boundary"];

export const CATEGORY_LABELS = {
  close: "Close catching",
  inner_ring: "Inner ring",
  boundary: "Boundary",
};

/** Fielders grouped for a legend, in `CATEGORY_ORDER`, empty groups dropped. */
export function groupFielders(model) {
  if (!model) return [];
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    fielders: model.fielders.filter((f) => f.category === category),
  })).filter((group) => group.fielders.length > 0);
}
